import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Save, Loader2, SlidersHorizontal, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

function fmtEuro(n) {
  return (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}


export default function ThresholdsSettings() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [autoBelow, setAutoBelow] = useState("");
  const [managerBelow, setManagerBelow] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const canEdit = hasPermission("tenant.update");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/purchases/approval-thresholds");
        setAutoBelow(String(r.data.thresholds?.auto_validate_below ?? 100));
        setManagerBelow(String(r.data.thresholds?.manager_below ?? 500));
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    const a = Number(autoBelow), m = Number(managerBelow);
    if (!(a >= 0) || !(m > 0)) { toast.error("Montants invalides."); return; }
    if (m <= a) { toast.error(t("thresholds.invalidOrder")); return; }
    setSaving(true);
    try {
      await api.patch("/purchases/approval-thresholds", {
        auto_validate_below: a, manager_below: m,
      });
      toast.success(t("thresholds.saved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const a = Number(autoBelow) || 0;
  const m = Number(managerBelow) || 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="thresholds-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
          {t("sidebar.thresholds")}
        </div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("thresholds.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("thresholds.subtitle")}</p>
      </div>

      <Card className="p-4 border-blue-200 bg-blue-50/40 rounded-xl">
        <div className="flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <div className="text-sm font-medium text-blue-900">
              {t("thresholds.gouvernanceTitle")}
            </div>
            <div className="text-sm text-blue-800/80 mt-1">
              {t("thresholds.gouvernanceDesc")}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-slate-200 rounded-xl bg-white space-y-5">
        {loading ? (
          <div className="text-sm text-slate-500">Chargement…</div>
        ) : (
          <>
            <div>
              <Label>{t("thresholds.autoLabel")}</Label>
              <Input type="number" min="0" step="10" value={autoBelow}
                     onChange={(e) => setAutoBelow(e.target.value)}
                     disabled={!canEdit}
                     data-testid="threshold-auto-input" />
              <p className="mt-1 text-xs text-slate-500">{t("thresholds.autoHelp")}</p>
            </div>
            <div>
              <Label>{t("thresholds.managerLabel")}</Label>
              <Input type="number" min="0" step="10" value={managerBelow}
                     onChange={(e) => setManagerBelow(e.target.value)}
                     disabled={!canEdit}
                     data-testid="threshold-manager-input" />
              <p className="mt-1 text-xs text-slate-500">{t("thresholds.managerHelp")}</p>
            </div>

            {/* Live summary */}
            <div className="border-t border-slate-100 pt-4">
              <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500 mb-3">
                Aperçu
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-emerald-800">
                    &lt; {fmtEuro(a)}
                  </div>
                  <div className="mt-1 font-medium text-emerald-900" data-testid="summary-auto">
                    {t("thresholds.summaryAuto")}
                  </div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-amber-800">
                    {fmtEuro(a)} – {fmtEuro(m)}
                  </div>
                  <div className="mt-1 font-medium text-amber-900" data-testid="summary-manager">
                    {t("thresholds.summaryManager")}
                  </div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50/40 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-widest text-red-800">
                    ≥ {fmtEuro(m)}
                  </div>
                  <div className="mt-1 font-medium text-red-900" data-testid="summary-director">
                    {t("thresholds.summaryDirector")}
                  </div>
                </div>
              </div>
            </div>

            {canEdit && (
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button onClick={save} disabled={saving}
                        data-testid="thresholds-save-btn">
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Enregistrement…</>
                  ) : (
                    <><Save className="h-4 w-4 mr-1.5" /> {t("thresholds.save")}</>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
